import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Switch, NumberInput } from '@/components/ui';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { useMethodSwapAnimation } from '../surcharge/useMethodSwapAnimation';
import { SurchargeMethodPanel } from '../surcharge/SurchargeMethodPanel';
import {
  computeZoneLayout as sharedComputeZoneLayout,
  SPAN_STYLES as SHARED_SPAN_STYLES,
} from './timeline/TimelinePrimitives';
import {
  TIMELINE_ANNOTATION_INLINE_CLASS,
  TIMELINE_AXIS_LABEL_CLASS,
  TIMELINE_AXIS_TICK_CLASS,
  TIMELINE_INK,
  type TimelineInkVariant,
  TIMELINE_NOTE_TEXT_CLASS,
  TIMELINE_SEGMENT_HEADING_CLASS,
  formatDeadlineTick,
} from './timeline/timelineTextTokens';
import { ScenarioTogglePanel, type ScenarioOption } from '../ScenarioTogglePanel';
import { ControlRow } from '../ControlRow';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface LadderOffsetComparisonProps {
  ladder: Array<{ code: string; multiplier: number; name?: string; isExtended?: boolean }>;
  deadlineHours: number;
  stepUpAfterMinutes: number;
  useAdditionalOffset: boolean;
  recurringDeadline: boolean;
  /** Number of ladder steps each violation shifts up. Defaults to 1. */
  overtimeOffset?: number;
  onUseAdditionalOffsetChange?: (value: boolean) => void;
  onStepUpAfterMinutesChange?: (value: number | null) => void;
  /** Extra control rows prepended to the shared control card (before the built-in Additional Offset + Step-Up Deadline rows). */
  leadingControls?: React.ReactNode;
  /** Extra control rows appended to the shared control card (after the built-in Additional Offset + Step-Up Deadline rows). */
  additionalControls?: React.ReactNode;
  /** Optional content rendered directly below the Surcharge Ladder reference strip's annotation paragraph, above the controls card. */
  ladderFooter?: React.ReactNode;
}

interface TimelineZone {
  key: string;
  /** Flex weight (proportional to duration in the segment) */
  flex: number;
  label?: string;
  offset?: string;
  /**
   * Two-line offset breakdown — top shows the active violation's contribution (red),
   * bottom shows carried-over offset from prior violations (amber). Takes precedence
   * over `offset` when set.
   */
  offsetStack?: { top: string; bottom: string };
  grossMultiplier?: string;
  variant: 'safe' | 'v1' | 'v1-stepup' | 'v2' | 'v2-stepup' | 'break' | 'ellipsis';
  /** Right border style override */
  dashedRight?: boolean;
  /** Solid left border for major section boundaries */
  solidLeft?: boolean;
}

interface ViolationSpan {
  key: string;
  label: string;
  leftPct: number;
  rightPct: number;
  variant: 'v1' | 'v2';
}

interface TimeAxisTick {
  key: string;
  label: string;
  leftPct: number;
  align: 'left' | 'center' | 'right';
  /** Color variant — routed through the shared `TIMELINE_INK` token map. */
  variant: Exclude<TimelineInkVariant, 'muted'>;
  /** Optional opacity (0–1) for fade-in/out transitions */
  opacity?: number;
}

interface TimelinePanel {
  zones: TimelineZone[];
  spans: ViolationSpan[];
  ticks: TimeAxisTick[];
}

/* ------------------------------------------------------------------ */
/*  Scenario computation                                               */
/* ------------------------------------------------------------------ */

function resolveOffset(
  baseRank: number,
  offset: number,
  maxRank: number,
): { resolvedRank: number; capped: boolean } {
  const target = baseRank + offset;
  if (target >= maxRank) {
    return { resolvedRank: maxRank, capped: target > maxRank };
  }
  return { resolvedRank: target, capped: false };
}

function formatStepUpDuration(minutes: number): string {
  if (minutes >= 60 && minutes % 60 === 0) return `+${minutes / 60}h`;
  if (minutes >= 60) return `+${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `+${minutes}m`;
}



function formatMultiplierValue(value: number, capped = false): string {
  const scaled = Math.round(value * 100);
  const str = scaled % 10 === 0 ? value.toFixed(1) : value.toFixed(2);
  return capped ? `${str}x cap` : `${str}x`;
}

/** Build the standard safe + v1-pre + v1-post zones used by multiple panel computations. */
/** Build the standard safe + v1-pre + v1-post zones used by multiple panel computations. */
function buildV1BaseZones(
  ladder: Array<{ code: string; multiplier: number }>,
  hasStepUp: boolean,
  overtimeOffset: number,
  keyPrefix = '',
): TimelineZone[] {
  const maxRank = ladder.length - 1;
  const baseMultiplier = ladder[0]?.multiplier ?? 1;
  const v1Pre = resolveOffset(0, overtimeOffset, maxRank);
  const v1Post = resolveOffset(0, overtimeOffset + 1, maxRank);
  const OFFSET_ZONE = 1;
  const p = keyPrefix;
  return [
    { key: `${p}safe`, flex: 1.2, label: 'Working', variant: 'safe' },
    { key: `${p}v1-pre`, flex: OFFSET_ZONE, offset: `+${overtimeOffset}`, grossMultiplier: formatMultiplierValue(ladder[v1Pre.resolvedRank]?.multiplier ?? baseMultiplier), variant: 'v1', dashedRight: hasStepUp },
    { key: `${p}v1-post`, flex: hasStepUp ? OFFSET_ZONE : 0, offset: `+${overtimeOffset + 1}`, grossMultiplier: formatMultiplierValue(ladder[v1Post.resolvedRank]?.multiplier ?? baseMultiplier), variant: 'v1-stepup' },
  ];
}

function computeSingleViolationPanel(
  ladder: Array<{ code: string; multiplier: number }>,
  deadlineHours: number,
  stepUpMinutes: number,
  hasStepUp: boolean,
  overtimeOffset: number,
): TimelinePanel {
  const zones = buildV1BaseZones(ladder, hasStepUp, overtimeOffset);
  const { lefts } = computeZoneLayout(zones);

  const spans: ViolationSpan[] = [
    { key: 'v1-span', label: 'Violation 1', leftPct: lefts[1], rightPct: 0, variant: 'v1' },
  ];

  const ticks: TimeAxisTick[] = [
    { key: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
    { key: 'v1', label: formatDeadlineTick(deadlineHours), leftPct: lefts[1], align: 'center', variant: 'amber' },
    { key: 'v1-step', label: formatStepUpDuration(stepUpMinutes), leftPct: lefts[2], align: 'center', variant: 'red', opacity: hasStepUp ? 1 : 0 },
    { key: 'end', label: 'End', leftPct: 100, align: 'right', variant: 'label' },
  ];

  return { zones, spans, ticks };
}


function computeWithoutBreakPanel(
  ladder: Array<{ code: string; multiplier: number }>,
  deadlineHours: number,
  stepUpMinutes: number,
  hasStepUp: boolean,
  overtimeOffset: number,
): TimelinePanel {
  const maxRank = ladder.length - 1;
  const baseMultiplier = ladder[0]?.multiplier ?? 1;
  const stepUpHours = stepUpMinutes / 60;
  const v2Start = deadlineHours * 2;

  // V2 cumulative offset: everything V1 contributed (incl. its step-up when enabled)
  const v2CumulativeBase = hasStepUp ? overtimeOffset + 1 : overtimeOffset;
  const v2PreOffset = v2CumulativeBase + overtimeOffset;
  const v2PostOffset = v2CumulativeBase + overtimeOffset + 1;
  const v2Pre = resolveOffset(0, v2PreOffset, maxRank);
  const v2Post = resolveOffset(0, v2PostOffset, maxRank);

  const OFFSET_ZONE = 1;

  const zones: TimelineZone[] = [
    ...buildV1BaseZones(ladder, hasStepUp, overtimeOffset),
    {
      key: 'v2-pre',
      flex: OFFSET_ZONE,
      offsetStack: { top: `+${overtimeOffset}`, bottom: `+${v2CumulativeBase}` },
      grossMultiplier: formatMultiplierValue(ladder[v2Pre.resolvedRank]?.multiplier ?? baseMultiplier, v2Pre.capped),
      variant: 'v2',
      dashedRight: hasStepUp,
      solidLeft: true,
    },
    {
      key: 'v2-post',
      flex: hasStepUp ? OFFSET_ZONE : 0,
      offsetStack: { top: `+${overtimeOffset + 1}`, bottom: `+${v2CumulativeBase}` },
      grossMultiplier: formatMultiplierValue(ladder[v2Post.resolvedRank]?.multiplier ?? baseMultiplier, v2Post.capped),
      variant: 'v2-stepup',
    },
    { key: 'ellipsis', flex: hasStepUp ? Math.max(stepUpHours, 0.8) : 0.5, offset: '...', variant: 'ellipsis' },
  ];

  const { lefts } = computeZoneLayout(zones);

  const spans: ViolationSpan[] = [
    { key: 'v2-span', label: 'Violation 2', leftPct: lefts[3], rightPct: 0, variant: 'v2' },
    { key: 'v1-span', label: 'Violation 1', leftPct: lefts[1], rightPct: 0, variant: 'v1' },
  ];

  const ticks: TimeAxisTick[] = [
    { key: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
    { key: 'v1', label: formatDeadlineTick(deadlineHours), leftPct: lefts[1], align: 'center', variant: 'amber' },
    { key: 'v1-step', label: formatStepUpDuration(stepUpMinutes), leftPct: lefts[2], align: 'center', variant: 'red', opacity: hasStepUp ? 1 : 0 },
    { key: 'v2', label: formatDeadlineTick(v2Start), leftPct: lefts[3], align: 'center', variant: 'amber' },
    { key: 'v2-step', label: formatStepUpDuration(stepUpMinutes), leftPct: lefts[4], align: 'center', variant: 'red', opacity: hasStepUp ? 1 : 0 },
    { key: 'end', label: 'End', leftPct: 100, align: 'right', variant: 'label' },
  ];

  return { zones, spans, ticks };
}

function computeWithBreakSegment1(
  ladder: Array<{ code: string; multiplier: number }>,
  deadlineHours: number,
  stepUpMinutes: number,
  hasStepUp: boolean,
  overtimeOffset: number,
): TimelinePanel {
  const zones: TimelineZone[] = [
    ...buildV1BaseZones(ladder, hasStepUp, overtimeOffset, 's1-'),
    { key: 's1-brk', flex: 1, label: 'Break', variant: 'break' },
  ];

  const { lefts } = computeZoneLayout(zones);
  const brkLeft = lefts[3];

  const spans: ViolationSpan[] = [
    { key: 's1-v1-span', label: 'Violation 1', leftPct: lefts[1], rightPct: 100 - brkLeft, variant: 'v1' },
  ];

  const ticks: TimeAxisTick[] = [
    { key: 's1-start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
    { key: 's1-v1', label: formatDeadlineTick(deadlineHours), leftPct: lefts[1], align: 'center', variant: 'amber' },
    { key: 's1-step', label: formatStepUpDuration(stepUpMinutes), leftPct: lefts[2], align: 'center', variant: 'red', opacity: hasStepUp ? 1 : 0 },
    { key: 's1-end', label: 'Break End', leftPct: 100, align: 'right', variant: 'label' },
  ];

  return { zones, spans, ticks };
}

function computeWithBreakSegment2(
  ladder: Array<{ code: string; multiplier: number }>,
  deadlineHours: number,
  stepUpMinutes: number,
  hasStepUp: boolean,
  overtimeOffset: number,
): TimelinePanel {
  const maxRank = ladder.length - 1;
  const baseMultiplier = ladder[0]?.multiplier ?? 1;

  // V2 triggers at deadline hours into segment 2 — ladder resets, so this
  // is identical in structure to the single-violation panel.
  const v2Pre = resolveOffset(0, overtimeOffset, maxRank);
  const v2Post = resolveOffset(0, overtimeOffset + 1, maxRank);

  const OFFSET_ZONE_S2 = 1;

  // Always 4 zones with stable keys; s2-v2-post collapses to flex:0 when no step-up.
  // Zones use v2 variants (red) since this is the second violation in the recurring sequence.
  const zones: TimelineZone[] = [
    { key: 's2-safe', flex: 1.2, label: 'Working', variant: 'safe' },
    { key: 's2-v2-pre', flex: OFFSET_ZONE_S2, offset: `+${overtimeOffset}`, grossMultiplier: formatMultiplierValue(ladder[v2Pre.resolvedRank]?.multiplier ?? baseMultiplier), variant: 'v2', dashedRight: hasStepUp },
    { key: 's2-v2-post', flex: hasStepUp ? OFFSET_ZONE_S2 : 0, offset: `+${overtimeOffset + 1}`, grossMultiplier: formatMultiplierValue(ladder[v2Post.resolvedRank]?.multiplier ?? baseMultiplier), variant: 'v2-stepup' },
    { key: 's2-ellipsis', flex: 0.5, offset: '...', variant: 'ellipsis' },
  ];

  const { lefts } = computeZoneLayout(zones);
  // Zone indices: 0=safe, 1=v2-pre, 2=v2-post, 3=ellipsis

  const spans: ViolationSpan[] = [
    { key: 's2-v2-span', label: `Violation 2 (reset to +${overtimeOffset})`, leftPct: lefts[1], rightPct: 0, variant: 'v2' },
  ];

  const ticks: TimeAxisTick[] = [
    { key: 's2-start', label: 'Break End', leftPct: 0, align: 'left', variant: 'label' },
    { key: 's2-v2', label: formatDeadlineTick(deadlineHours), leftPct: lefts[1], align: 'center', variant: 'amber' },
    { key: 's2-step', label: formatStepUpDuration(stepUpMinutes), leftPct: lefts[2], align: 'center', variant: 'red', opacity: hasStepUp ? 1 : 0 },
    { key: 's2-end', label: 'End', leftPct: 100, align: 'right', variant: 'label' },
  ];

  return { zones, spans, ticks };
}

/* ------------------------------------------------------------------ */
/*  TimelineBar                                                        */
/* ------------------------------------------------------------------ */

const ZONE_STYLES: Record<TimelineZone['variant'], {
  bg: string;
  border?: string;
  labelColor: string;
  offsetColor: string;
  grossColor: string;
}> = {
  safe: {
    bg: 'bg-scenario-green/[0.08]',
    border: 'border-r-2 border-r-scenario-green/30',
    labelColor: 'color-mix(in srgb, var(--color-scenario-green), white 35%)',
    offsetColor: 'color-mix(in srgb, var(--color-scenario-green), white 30%)',
    grossColor: 'color-mix(in srgb, var(--color-scenario-green), white 28%)',
  },
  v1: {
    bg: 'bg-primary/[0.06]',
    labelColor: 'color-mix(in srgb, var(--primary-text), transparent 40%)',
    offsetColor: 'var(--primary-text)',
    grossColor: 'color-mix(in srgb, var(--primary-text), transparent 50%)',
  },
  'v1-stepup': {
    bg: 'bg-primary/[0.10]',
    labelColor: 'color-mix(in srgb, var(--primary-text), transparent 30%)',
    offsetColor: 'var(--primary-text)',
    grossColor: 'color-mix(in srgb, var(--primary-text), transparent 40%)',
  },
  v2: {
    bg: 'bg-destructive/[0.08]',
    labelColor: 'color-mix(in srgb, var(--color-destructive), transparent 40%)',
    offsetColor: 'var(--destructive-text)',
    grossColor: 'color-mix(in srgb, var(--color-destructive), transparent 50%)',
  },
  'v2-stepup': {
    bg: 'bg-destructive/[0.12]',
    labelColor: 'color-mix(in srgb, var(--color-destructive), transparent 30%)',
    offsetColor: 'var(--destructive-text)',
    grossColor: 'color-mix(in srgb, var(--color-destructive), transparent 40%)',
  },
  break: {
    bg: 'bg-scenario-green/[0.04]',
    border: undefined,
    labelColor: 'color-mix(in srgb, var(--color-scenario-green), transparent 60%)',
    offsetColor: 'color-mix(in srgb, var(--color-scenario-green), transparent 60%)',
    grossColor: 'color-mix(in srgb, var(--color-scenario-green), transparent 60%)',
  },
  ellipsis: {
    bg: 'bg-destructive/[0.06]',
    border: 'border-l-2 border-l-scenario-green/30',
    labelColor: 'color-mix(in srgb, var(--color-destructive), transparent 60%)',
    offsetColor: 'color-mix(in srgb, var(--color-destructive), transparent 50%)',
    grossColor: 'color-mix(in srgb, var(--color-destructive), transparent 60%)',
  },
};

function computeZoneLayout(zones: TimelineZone[]): { widths: number[]; lefts: number[] } {
  return sharedComputeZoneLayout(zones, { minPct: 13 });
}

const TimelineBar: React.FC<{ zones: TimelineZone[] }> = ({ zones }) => {
  const { widths: adjustedPcts, lefts: leftPcts } = computeZoneLayout(zones);

  return (
  <div
    className="relative h-14 rounded-[0.25rem] overflow-hidden"
    style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--border), transparent 40%)' }}
  >
    {zones.map((zone, i) => {
      const style = ZONE_STYLES[zone.variant];
      const isCollapsed = zone.flex === 0;
      return (
        <div
          key={zone.key}
          className={cn(
            'absolute top-0 bottom-0 flex flex-col items-center justify-center min-w-0 gap-1 px-1 overflow-hidden',
            'motion-safe:transition-[left,width,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]',
            style.bg,
            !zone.dashedRight && (i < zones.length - 1 || zone.variant === 'ellipsis') && style.border,
          )}
          style={{
            width: `${adjustedPcts[i]}%`,
            left: `${leftPcts[i]}%`,
            opacity: isCollapsed ? 0 : 1,
            ...(zone.variant === 'break' ? {
              borderLeft: '2px solid color-mix(in srgb, var(--color-scenario-green), transparent 70%)',
              borderRight: i < zones.length - 1
                ? '2px solid color-mix(in srgb, var(--color-scenario-green), transparent 70%)'
                : undefined,
            } : {}),
          }}
        >
          {/* Solid left border for V2 boundary — always rendered, opacity-animated */}
          <div
            className="absolute left-0 top-0 bottom-0 w-0 pointer-events-none motion-safe:transition-opacity motion-safe:duration-300"
            style={{
              borderLeft: `2px solid color-mix(in srgb, var(--color-scenario-green), transparent 70%)`,
              opacity: zone.solidLeft ? 1 : 0,
            }}
          />
          {/* Dashed right border as overlay — always rendered, opacity-animated */}
          <div
            className="absolute right-0 top-0 bottom-0 w-px pointer-events-none motion-safe:transition-opacity motion-safe:duration-300"
            style={{
              borderRight: `1px dashed color-mix(in srgb, ${zone.variant === 'v1' || zone.variant === 'v1-stepup' ? 'var(--primary)' : 'var(--color-destructive)'}, transparent 75%)`,
              opacity: zone.dashedRight ? 1 : 0,
            }}
          />
          {zone.label && (
            <span
              className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.3px] pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
              style={{ color: style.labelColor }}
            >
              {zone.label}
            </span>
          )}
          {zone.offsetStack && (
            <div className="flex flex-col items-center justify-center gap-0.5 pointer-events-none">
              <span
                className="font-mono text-[10px] sm:text-[12px] font-semibold leading-none whitespace-nowrap overflow-hidden text-ellipsis"
                style={{ color: 'var(--destructive-text)' }}
              >
                {zone.offsetStack.top}
              </span>
              <span
                className="font-mono text-[10px] sm:text-[12px] font-semibold leading-none whitespace-nowrap overflow-hidden text-ellipsis"
                style={{ color: 'var(--primary-text)' }}
              >
                {zone.offsetStack.bottom}
              </span>
            </div>
          )}
          {zone.offset && !zone.offsetStack && (
            <span
              className="font-mono text-[10px] sm:text-[12px] font-semibold leading-none pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
              style={{ color: style.offsetColor }}
            >
              {zone.offset}
            </span>
          )}
          {zone.grossMultiplier && !zone.offset && !zone.offsetStack && (
            <span
              className="font-mono text-[10px] sm:text-[12px] leading-none pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
              style={{ color: style.grossColor }}
            >
              {zone.grossMultiplier}
            </span>
          )}
        </div>
      );
    })}
  </div>
  );
};

/* ------------------------------------------------------------------ */
/*  ViolationSpans                                                     */
/* ------------------------------------------------------------------ */

const SpanBar: React.FC<{ span: ViolationSpan }> = ({ span }) => {
  const s = SHARED_SPAN_STYLES[span.variant];
  return (
    <div
      className="absolute top-0 h-full flex items-center justify-center rounded-t-[0.25rem] motion-safe:transition-[left,right] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]"
      style={{
        left: span.leftPct > 0 ? `calc(${span.leftPct}% - 2px)` : '0%',
        right: span.rightPct > 0 ? `calc(${span.rightPct}% - 2px)` : '0%',
        background: s.bg,
        borderTop: `1px solid ${s.border}`,
        borderLeft: `1px solid ${s.border}`,
        borderRight: `1px solid ${s.border}`,
      }}
    >
      <span
        className="font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] font-semibold whitespace-nowrap"
        style={{ color: s.labelColor }}
      >
        {span.label}
      </span>
    </div>
  );
};

const ViolationSpans: React.FC<{ spans: ViolationSpan[]; stacked?: boolean }> = ({ spans, stacked = false }) => {
  if (spans.length === 0) return null;

  if (stacked) {
    return (
      <div className="flex flex-col w-full" style={{ gap: 0, marginBottom: 0 }}>
        {spans.map((span) => (
          <div key={span.key} className="relative h-[14px] w-full">
            <SpanBar span={span} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative h-[16px] w-full">
      {spans.map((span) => (
        <SpanBar key={span.key} span={span} />
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  TimeAxis                                                           */
/* ------------------------------------------------------------------ */

const TICK_STYLES: Record<TimeAxisTick['variant'], string> = {
  label: `${TIMELINE_AXIS_LABEL_CLASS} whitespace-nowrap`,
  amber: `${TIMELINE_AXIS_TICK_CLASS} font-normal whitespace-pre text-center`,
  red: `${TIMELINE_AXIS_TICK_CLASS} font-normal whitespace-pre text-center`,
  green: `${TIMELINE_AXIS_TICK_CLASS} font-normal whitespace-nowrap`,
};

const TimeAxis: React.FC<{ ticks: TimeAxisTick[] }> = ({ ticks }) => (
  <div className="relative h-[32px] pt-1">
    {ticks.map((tick) => {
      const transform =
        tick.align === 'left'
          ? 'none'
          : tick.align === 'right'
            ? 'translateX(-100%)'
            : 'translateX(-50%)';
      const color = TIMELINE_INK[tick.variant];
      const tickOpacity = tick.opacity ?? 1;
      return (
        <span
          key={tick.key}
          className={cn('absolute motion-safe:transition-[left,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.4,0,0.2,1)]', TICK_STYLES[tick.variant])}
          style={{
            left: `${tick.leftPct}%`,
            transform,
            opacity: tickOpacity,
            ...(color ? { color } : {}),
          }}
        >
          {tick.label}
        </span>
      );
    })}
  </div>
);

/* ------------------------------------------------------------------ */
/*  LadderOffsetComparison (exported)                                  */
/* ------------------------------------------------------------------ */

const LadderReferenceStrip: React.FC<{
  ladder: Array<{ code: string; multiplier: number; name?: string; isExtended?: boolean }>;
  overtimeOffset: number;
  recurringDeadline?: boolean;
}> = ({ ladder, overtimeOffset, recurringDeadline = false }) => {
  if (ladder.length < 2) return null;
  const stepNoun = overtimeOffset === 1 ? 'step' : 'steps';
  const baseLadder = ladder.filter((t) => !t.isExtended);
  const extendedLadder = ladder.filter((t) => t.isExtended);
  const hasExtended = extendedLadder.length > 0;

  const renderCell = (
    tier: { code: string; multiplier: number; isExtended?: boolean },
    isBaseRate = false,
  ) => (
    <motion.div
      layout="position"
      key={`${tier.code}-cell`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        opacity: { duration: 0.18, ease: 'easeOut' },
        layout: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
      }}
      className="flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-1 py-1.5"
    >
      <span
        className={cn(
          'font-mono text-[15px] sm:text-[16px] font-bold tabular-nums leading-none',
          isBaseRate && 'line-through',
        )}
        style={{
          color: isBaseRate
            ? 'var(--muted-foreground)'
            : tier.isExtended
              ? 'var(--primary-text)'
              : 'var(--foreground)',
        }}
      >
        {formatMultiplierValue(tier.multiplier)}
      </span>
    </motion.div>
  );

  const renderArrow = (key: string, isTransition = false) => (
    <motion.div
      layout="position"
      key={key}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        opacity: { duration: 0.15, ease: 'easeOut' },
        layout: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
      }}
      className={cn(
        'relative flex items-center justify-center px-1 sm:px-2 shrink-0',
        isTransition && '-mr-[11px] sm:-mr-4 z-10',
      )}
      aria-hidden="true"
    >
      <ArrowRight
        className="h-3.5 w-3.5 sm:h-4 sm:w-4"
        style={{ color: 'var(--primary-text)' }}
      />
      <span
        className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+0.75rem)] font-mono text-[10px] sm:text-[11px] font-semibold leading-none"
        style={{ color: 'var(--primary-text)' }}
      >
        +1
      </span>
    </motion.div>
  );

  const extendedBorderColor = 'color-mix(in srgb, var(--primary), transparent 40%)';

  return (
    <div
      role="img"
      aria-label={`Rate ladder reference: each violation shifts up ${overtimeOffset} ${stepNoun}`}
    >
      <div className="flex items-center gap-2 mt-2 mb-3">
        <div className="flex-1 h-px bg-border/40" />
        <span className="font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] whitespace-nowrap text-muted-foreground">
          Surcharge Ladder
        </span>
        <div className="flex-1 h-px bg-border/40" />
      </div>
      <div className="flex items-stretch w-full mb-5">
        <div
          className={cn(
            'flex items-stretch bg-surface-2/30 border border-border/40 py-2 min-w-0',
            hasExtended
              ? 'rounded-l-[0.25rem] border-r-0 pl-2 pr-0'
              : 'rounded-[0.25rem] px-2',
          )}
          style={{ flex: baseLadder.length }}
        >
          <AnimatePresence mode="popLayout" initial={false}>
            {baseLadder.flatMap((tier, i) => {
              const nodes: React.ReactNode[] = [renderCell(tier, i === 0)];
              const isLastBase = i === baseLadder.length - 1;
              if (!isLastBase || hasExtended) {
                const isTransition = isLastBase && hasExtended;
                nodes.push(renderArrow(`${tier.code}-arrow`, isTransition));
              }
              return nodes;
            })}
          </AnimatePresence>
        </div>
        <AnimatePresence mode="popLayout" initial={false}>
          {hasExtended && (
            <motion.div
              key="extended-section"
              layout
              initial={{ opacity: 0, flex: 0 }}
              animate={{ opacity: 1, flex: extendedLadder.length }}
              exit={{ opacity: 0, flex: 0 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              className="flex items-stretch bg-primary/[0.06] border border-dashed rounded-r-[0.25rem] py-2 pr-2 pl-0 min-w-0"
              style={{ borderColor: extendedBorderColor }}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                {extendedLadder.flatMap((tier, i) => {
                  const nodes: React.ReactNode[] = [renderCell(tier)];
                  if (i < extendedLadder.length - 1) {
                    nodes.push(renderArrow(`${tier.code}-arrow`));
                  }
                  return nodes;
                })}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <p className={cn(TIMELINE_ANNOTATION_INLINE_CLASS, recurringDeadline ? 'mb-0' : 'mb-2')}>
        If the offset exceeds the highest tier on your overtime ladder, the surcharge is capped at that top tier. The <strong>Extended Ladder</strong> can serve as additional surcharge tiers.
      </p>
    </div>
  );
};

export const LadderOffsetComparison: React.FC<LadderOffsetComparisonProps> = ({
  ladder,
  deadlineHours,
  stepUpAfterMinutes,
  useAdditionalOffset,
  recurringDeadline,
  overtimeOffset = 1,
  onUseAdditionalOffsetChange,
  onStepUpAfterMinutesChange,
  leadingControls,
  additionalControls,
  ladderFooter,
}) => {
  const resolvedOvertimeOffset = Math.max(1, Math.floor(overtimeOffset));
  const [recurringScenario, setRecurringScenario] = useState<'no-break' | 'break'>('no-break');

  const {
    containerStyle: recurringContainerStyle,
    containerRef: recurringContainerRef,
    activePanelRef: recurringPanelRef,
    fadeDuration: recurringFadeDuration,
  } = useMethodSwapAnimation(recurringScenario);

  // Guard: need at least 1 tier and a positive deadline to render
  if (ladder.length === 0 || deadlineHours <= 0) return null;

  // Clamp step-up to avoid negative flex values:
  // step-up must be shorter than the gap between violations (deadlineHours)
  const effectiveStepUpMinutes = Math.min(stepUpAfterMinutes, (deadlineHours * 60) - 1);
  const hasStepUp = useAdditionalOffset && effectiveStepUpMinutes > 0;
  const additionalOffsetAnnotation = (
    <AnimatedCollapse open={useAdditionalOffset} duration={250}>
      <p className={TIMELINE_ANNOTATION_INLINE_CLASS}>
        When the <strong className="font-semibold text-foreground">Additional Offset</strong> is enabled, the tier jump increases from +{resolvedOvertimeOffset} to +{resolvedOvertimeOffset + 1} after the <strong className="font-semibold text-foreground">Step-Up Deadline</strong> threshold is reached with no provided meal break.
      </p>
    </AnimatedCollapse>
  );

  // Build graphic content based on mode
  let graphicContent: React.ReactNode;

  if (!recurringDeadline) {
    // Single violation mode — no break comparison needed since breaks
    // don't reset anything when recurring deadline is off
    const singlePanel = computeSingleViolationPanel(ladder, deadlineHours, effectiveStepUpMinutes, hasStepUp, resolvedOvertimeOffset);
    graphicContent = (
      <>
        <ViolationSpans spans={singlePanel.spans} />
        <TimelineBar zones={singlePanel.zones} />
        <TimeAxis ticks={singlePanel.ticks} />
      </>
    );
  } else {
    // Recurring mode: button-toggled scenario swap
    const noBreakPanel = computeWithoutBreakPanel(ladder, deadlineHours, effectiveStepUpMinutes, hasStepUp, resolvedOvertimeOffset);
    const seg1 = computeWithBreakSegment1(ladder, deadlineHours, effectiveStepUpMinutes, hasStepUp, resolvedOvertimeOffset);
    const seg2 = computeWithBreakSegment2(ladder, deadlineHours, effectiveStepUpMinutes, hasStepUp, resolvedOvertimeOffset);

    const scenarioOptions = [
      {
        key: 'no-break' as const,
        label: 'No Break Taken',
        symbol: '\u26A0',
        description: 'Violations compound — each one climbs higher on the ladder',
      },
      {
        key: 'break' as const,
        label: 'Break Taken',
        symbol: '\u2713',
        description: `Break resets the ladder — next violation starts fresh at +${resolvedOvertimeOffset} instead of +${hasStepUp ? resolvedOvertimeOffset * 2 + 1 : resolvedOvertimeOffset * 2}`,
      },
    ] satisfies ScenarioOption<'no-break' | 'break'>[];

    graphicContent = (
      <ScenarioTogglePanel<'no-break' | 'break'>
        ariaLabel="Recurring Surcharge Scenario"
        options={scenarioOptions}
        scenario={recurringScenario}
        onScenarioChange={setRecurringScenario}
      >
        <div className="w-full">
          <div
            ref={recurringContainerRef}
            className="overflow-hidden"
            style={recurringContainerStyle}
          >
            <div className="relative grid">
              <AnimatePresence mode="sync" initial={false}>
                {recurringScenario === 'no-break' && (
                  <SurchargeMethodPanel
                    key="no-break"
                    activePanelRef={recurringPanelRef}
                    transitionDuration={recurringFadeDuration}
                  >
                    <ViolationSpans spans={noBreakPanel.spans} stacked />
                    <TimelineBar zones={noBreakPanel.zones} />
                    <TimeAxis ticks={noBreakPanel.ticks} />
                  </SurchargeMethodPanel>
                )}

                {recurringScenario === 'break' && (
                  <SurchargeMethodPanel
                    key="break"
                    activePanelRef={recurringPanelRef}
                    transitionDuration={recurringFadeDuration}
                  >
                    <div className={`${TIMELINE_SEGMENT_HEADING_CLASS} mb-1`}>
                      Work Segment 1
                    </div>
                    <ViolationSpans spans={seg1.spans} />
                    <TimelineBar zones={seg1.zones} />
                    <TimeAxis ticks={seg1.ticks} />

                    <div className="flex items-center gap-2 my-1">
                      <div className="flex-1 h-px bg-scenario-green/15" />
                      <span className="font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] whitespace-nowrap text-scenario-green/80">
                        &#8634; Ladder resets to +1
                      </span>
                      <div className="flex-1 h-px bg-scenario-green/15" />
                    </div>

                    <div className={`${TIMELINE_SEGMENT_HEADING_CLASS} mb-1`}>
                      Work Segment 2
                    </div>
                    <ViolationSpans spans={seg2.spans} />
                    <TimelineBar zones={seg2.zones} />
                    <TimeAxis ticks={seg2.ticks} />
                  </SurchargeMethodPanel>
                )}
              </AnimatePresence>
            </div>
          </div>
          {additionalOffsetAnnotation}
          <LadderReferenceStrip ladder={ladder} overtimeOffset={resolvedOvertimeOffset} recurringDeadline />
          {ladderFooter}
        </div>
      </ScenarioTogglePanel>
    );
  }

  const hasControls = onUseAdditionalOffsetChange || onStepUpAfterMinutesChange || leadingControls != null || additionalControls != null;

  return (
    <div role="group" aria-label="Ladder offset comparison">
      {graphicContent}
      {!recurringDeadline && additionalOffsetAnnotation}
      {!recurringDeadline && (
        <LadderReferenceStrip ladder={ladder} overtimeOffset={resolvedOvertimeOffset} />
      )}
      {!recurringDeadline && ladderFooter}

      {/* Controls */}
      {hasControls && (
        <div className="rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
          {leadingControls}
          {onUseAdditionalOffsetChange && (
            <ControlRow label="Additional Offset" description="After a set time, apply an additional offset up the rate ladder for a stronger surcharge.">
              <Switch
                checked={useAdditionalOffset}
                onCheckedChange={onUseAdditionalOffsetChange}
                aria-label="Toggle additional offset"
              />
            </ControlRow>
          )}

          {onStepUpAfterMinutesChange && (
            <AnimatedCollapse open={useAdditionalOffset} duration={250}>
              <ControlRow label="Step-Up Deadline" description="How long a missed break surcharge runs at the initial rate before stepping up to the next level.">
                <NumberInput
                  aria-label="Step-up deadline"
                  unit="min"
                  size="xs"
                  touchTarget
                  value={stepUpAfterMinutes || ''}
                  disabled={!useAdditionalOffset}
                  onChange={(e) => {
                    const v = e.target.value;
                    onStepUpAfterMinutesChange(v === '' ? null : Math.max(0, parseInt(v, 10) || 0));
                  }}
                />
              </ControlRow>
            </AnimatedCollapse>
          )}

          {additionalControls}
        </div>
      )}
    </div>
  );
};
